import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useMutation } from 'react-relay';
import { NumberFormattingChangeboardingWrapper } from '@jira/number-currency-formatting-changeboarding-flag/src/index.tsx';
import { setIsViewConfigModifiedRecord } from '@jira/issue-navigator__saved-views-utils/src/setIsViewConfigModifiedRecord.tsx';
import type { SelectorStoreUpdater } from 'relay-runtime';
import { Box, xcss } from '@atlaskit/primitives';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { isLoaderErrorAttributes } from '@atlassian/jira-errors-handling/src/utils/is-loader-error-attributes.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { type FlagConfiguration, useFlagService } from '@atlassian/jira-flags';
import AsyncAttachFileDialog from '@atlassian/jira-issue-operations-attach-file-dialog/src/ui/async.tsx';
import { DialogContextContainer } from '@atlassian/jira-issue-operations-dialog-context-provider/src/index.tsx';
import { getOperationScope } from '@atlassian/jira-issue-table-common/src/utils/index.tsx';
import type { SelectValue } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import {
	REMOVE_COLUMNS,
	ADD_COLUMNS,
} from '@atlassian/jira-issue-table-column-picker/src/common/constants.tsx';
import {
	REORDER_COLUMNS,
	RESTORE_COLUMNS,
	MAX_COLUMNS,
	MODIFY_COLUMNS,
} from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import { exclusiveGroupsOrIssuesFragment } from '@atlassian/jira-native-issue-table/src/common/utils/index.tsx';
import { useColumnWidthUpdater } from '@atlassian/jira-native-issue-table/src/controllers/use-column-width-updater/index.tsx';
import { useColumnIsFrozenUpdater } from '@atlassian/jira-native-issue-table/src/controllers/use-column-is-frozen-updater/index.tsx';
import AsyncNativeIssueTable from '@atlassian/jira-native-issue-table/src/ui/async.tsx';
import type { Props as IssueTableProps } from '@atlassian/jira-native-issue-table/src/ui/types.tsx';
import type { ConnectionsSubscriberProps } from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { main_issueNavigator_ListView_filter$key as FilterFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_ListView_filter.graphql';
import type { main_issueNavigator_ListView_groupResults$key as GroupResultsFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_ListView_groupResults.graphql';
import type { main_issueNavigator_ListView_issueResults$key as IssueResultsFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_ListView_issueResults.graphql';
import type { main_issueNavigator_ListView_project$key as ProjectFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_ListView_project.graphql';
import type { main_issueNavigator_ListView_view$key as ViewFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_ListView_view.graphql';
import type { main_issueNavigator_ListView_rankField$key as RankFieldFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_ListView_rankField.graphql';
import type {
	main_replaceSpreadsheetViewFieldSetsMutation$data,
	main_replaceSpreadsheetViewFieldSetsMutation as ReplaceSpreadsheetViewFieldSetsMutation,
} from '@atlassian/jira-relay/src/__generated__/main_replaceSpreadsheetViewFieldSetsMutation.graphql';
import type { main_replaceSpreadsheetViewFieldSetsOldMutation as ReplaceSpreadsheetViewFieldSetsOldMutation } from '@atlassian/jira-relay/src/__generated__/main_replaceSpreadsheetViewFieldSetsOldMutation.graphql';
import type { main_updateFieldSetPreferencesOldMutation as UpdateFieldSetPreferencesOldMutation } from '@atlassian/jira-relay/src/__generated__/main_updateFieldSetPreferencesOldMutation.graphql';
import type { main_updateFieldSetPreferencesMutation as UpdateFieldSetPreferencesMutation } from '@atlassian/jira-relay/src/__generated__/main_updateFieldSetPreferencesMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import UFOLabel from '@atlassian/jira-ufo-label/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { FetchError } from '@atlassian/jira-fetch';
import { useIssueNavigatorFeatures } from '../../../controllers/features-context/index.tsx';
import { MAX_ISSUES_PER_PAGE, PACKAGE_NAME, TEAM_NAME, views } from '../../../common/constants.tsx';
import type { OverridableIssueTableProps, SortDirection } from '../../../common/types.tsx';
import { withReportErrors } from '../../../common/ui/with-report-errors/index.tsx';
import {
	isFilterViewId,
	parseIssueNavigatorViewIdOrDefault,
} from '../../../common/utils/index.tsx';
import { markOnce, marks } from '../../../common/utils/performance-analytics.tsx';
import {
	useColumnPickerLoadingContext,
	ColumnPickerLoadingContextProvider,
} from '../../../controllers/column-picker-loading-context/index.tsx';
import { ColumnPickerMessageContext } from '../../../controllers/column-picker-message-context/index.tsx';
import {
	useSelectedIssueIndex,
	useSelectedIssueKey,
} from '../../../controllers/selected-issue/hooks.tsx';
import { useSelectedViewState } from '../../../controllers/selected-view-state/index.tsx';
import { useSortFieldAndDirection } from '../../../controllers/sort-field-and-direction-state/index.tsx';
import { useActiveJql, useResolvedJql } from '../../../services/active-jql/index.tsx';
import { useColumnLoader } from '../../../services/fetch-column-picker-options/index.tsx';
import { useInfiniteScroll } from '../../../services/infinite-scroll/index.tsx';
import { useIssueSearchActions } from '../../../services/issue-search/selectors.tsx';
import { useIFCSidebar } from '../../../common/ui/inline-field-config/sidebar/index.tsx';
import { useCommitFieldSetPreferences } from './utils/use-commit-field-set-preferences.tsx';
import ColumnPickerPopupComponent, {
	createPopupComponent,
} from './column-picker-popup-component/index.tsx';
import { ColumnPickerRemountManager } from './column-picker-remount-manager/index.tsx';
import { BulkOpsConnectionsSubscriber } from './bulk-operations-connections-subscriber/index.tsx';
import { IssueCount } from './issue-count/index.tsx';
import messages from './messages.tsx';
import { NoColumns } from './no-columns/index.tsx';
import { NoIssues } from './no-issues/index.tsx';
import { AsyncRegisterShortcutDialogActions } from './register-shortcut-dialog-actions/async.tsx';
import { dragAndDropExperience } from './utils/drag-and-drop-experience.tsx';
import { useEventHandlers } from './utils/use-event-handlers.tsx';

export type Props = {
	project: ProjectFragment | null;
	rankField: RankFieldFragment | null;
	loading: boolean;
	view: ViewFragment;
	onSelectedRowChange?: (rowIndex: number) => void;
	onChangeColumnConfiguration?: () => void;
	onIssueFlatListUpdated?: (
		newFlatKeyList: string[],
		newFlatIdList: string[],
		newFlatChildIdList: string[],
	) => void;
	issueTableProps?: OverridableIssueTableProps;
	isIssueHierarchySupportEnabled?: boolean;
	isIssueRankingSupportEnabled?: boolean;
	isMergedColumnSupportEnabled?: boolean;
	isFrozenColumnSupportEnabled?: boolean;
	isInBetweenColumnPickerEnabled?: boolean;
	isInlineFieldConfigEnabled?: boolean;
	filter: FilterFragment | null;
	viewResultError?: FetchError;
} & (
	| {
			groupResults: GroupResultsFragment;
			issueResults: null;
	  }
	| { groupResults: null; issueResults: IssueResultsFragment }
);

const reorderColumnsErrorFlag: FlagConfiguration = {
	type: 'error',
	title: messages.reorderColumnsErrorTitle,
	description: messages.columnConfigMutationErrorDescription,
	isAutoDismiss: true,
	messageId: 'issue-navigator.ui.issue-results.list-view.reorder-columns-error-flag.error',
	messageType: 'transactional',
};

const resetColumnsErrorFlag: FlagConfiguration = {
	type: 'error',
	title: messages.resetColumnsErrorTitle,
	description: messages.columnConfigMutationErrorDescription,
	isAutoDismiss: true,
	messageId: 'issue-navigator.ui.issue-results.list-view.reset-columns-error-flag.error',
	messageType: 'transactional',
};

const TableContainer = ({ children }: { children: React.ReactNode }) =>
	getWillShowNav4() ? <Box xcss={TableContainerStyles}>{children}</Box> : children;

export const useCanEditColumnConfiguration = (): boolean => {
	const isAnonymous = useIsAnonymous();
	const { isFilterEditable } = useActiveJql();
	const [{ view }] = useSelectedViewState();

	const { isLoading } = useColumnPickerLoadingContext();

	return !isAnonymous && (!isFilterViewId(view) || isFilterEditable === true) && !isLoading;
};

export const useConditionalOnRestoreDefaults = (
	onRestoreDefaultColumns: () => void,
	hasDefaultFieldSets: boolean | null | undefined,
	viewdataId?: string,
) => {
	const { isFilterEditable } = useActiveJql();
	const [{ view: selectedView }] = useSelectedViewState();

	if (fg('jira_inline_field_config_gate')) {
		return !(isFilterViewId(selectedView) && isFilterEditable === false) &&
			hasDefaultFieldSets === false &&
			// check that the view id from state and store is the same
			viewdataId === selectedView
			? onRestoreDefaultColumns
			: undefined;
	}

	return !(isFilterViewId(selectedView) && isFilterEditable === false) &&
		hasDefaultFieldSets === false
		? onRestoreDefaultColumns
		: undefined;
};

/**
 * the presence of conditionalOnChange determines whether the user has column configuration capabilities.
 * hence we only define conditionalOnChange hook when a user can edit a column
 */
export const useConditionalOnChange = (
	onColumnsChange: NonNullable<IssueTableProps['onColumnsChange']>,
) => {
	const canEditColumns = useCanEditColumnConfiguration();
	return canEditColumns ? onColumnsChange : undefined;
};

export const useColumnLoaderForSelectedView = (
	selectedColumns: SelectValue,
	isIFCEnabled?: boolean,
	projectContext?: string,
	savedViewId?: string | null,
) => {
	let [{ view: selectedView }] = useSelectedViewState();
	const canEditColumns = useCanEditColumnConfiguration();

	if (projectContext != null && fg('saved_list_view_technical_changes')) {
		selectedView = 'list_default';
	}

	return useColumnLoader(
		selectedView,
		canEditColumns,
		selectedColumns,
		fg('jira_inline_field_config_gate'),
		isIFCEnabled,
		projectContext,
		savedViewId,
	);
};

/**
 * only a logged in user can view the column picker,
 * hence we only define the useColumnLoader hook for logged in users.
 */
export const useConditionalColumnLoader = () => {
	const isAnonymous = useIsAnonymous();

	// eslint-disable-next-line react-hooks/react-compiler
	return !isAnonymous ? useColumnLoaderForSelectedView : undefined;
};

const ListView = ({
	loading,
	onChangeColumnConfiguration,
	onSelectedRowChange,
	issueResults,
	groupResults,
	view,
	project,
	rankField,
	issueTableProps,
	isIssueHierarchySupportEnabled,
	isIssueRankingSupportEnabled,
	isMergedColumnSupportEnabled,
	isFrozenColumnSupportEnabled,
	isInlineFieldConfigEnabled,
	isInBetweenColumnPickerEnabled,
	filter,
	onIssueFlatListUpdated,
	viewResultError,
}: Props) => {
	markOnce(marks.ISSUE_RESULTS_LIST_VIEW_START);

	useLayoutEffect(() => {
		markOnce(marks.ISSUE_RESULTS_LIST_VIEW_END);
	}, []);
	const resolvedJql = useResolvedJql();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const features = useIssueNavigatorFeatures();
	const isRowContextMenuEnabled = features.isRowContextMenuEnabled;
	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	const issueResultsData = useFragment<IssueResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_ListView_issueResults on JiraIssueConnection
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isReparentingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-list-reparenting.relayprovider"
				}
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isBulkOpsSelectAllEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/bulk-ops-select-all.relayprovider"
				}
				isFormulaFieldD1Enabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/frontier-formula-field-d1.relayprovider"
				}
			) {
				__id
				...registerShortcutDialogActions_issueNavigator_RegisterShortcutsDialogActions
				...NativeIssueTableWithSelectedIssuesProvider_issues
					@arguments(
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isReparentingEnabled: $isReparentingEnabled
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
					)
				pageCursors(maxCursors: 7) @skip(if: $isBulkOpsSelectAllEnabled) {
					around {
						pageNumber
						isCurrent
					}
				}
				issueNavigatorPageInfo @include(if: $isBulkOpsSelectAllEnabled) {
					firstIssuePosition
				}
				...ui_issueNavigatorBulkOperationsConnectionsSubscriber_BulkOpsConnectionsSubscriber
				...InlineFieldConfigContentCreate @include(if: $isFormulaFieldD1Enabled)
				...InlineFieldConfigContentEdit @include(if: $isFormulaFieldD1Enabled)
			}
		`,
		issueResults,
	);

	const groupResultsData = useFragment<GroupResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_ListView_groupResults on JiraSpreadsheetGroupConnection
			@argumentDefinitions(
				isPaginating: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
			) {
				...NativeIssueTableWithSelectedIssuesProvider_group
					@arguments(
						isPaginating: $isPaginating
						projectKeys: $projectKeys
						projectKey: $projectKey
						viewConfigInput: $viewConfigInput
					)
				firstGroup @skip(if: $isPaginating) {
					issues(
						viewConfigInput: $viewConfigInput
						fieldSetsInput: $fieldSetsInput
						scope: $fieldSetsScope
						first: 50
						settings: $settings
					) {
						...ui_issueNavigatorBulkOperationsConnectionsSubscriber_BulkOpsConnectionsSubscriber
					}
				}
			}
		`,
		groupResults,
	);

	const viewData = useFragment<ViewFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_ListView_view on JiraIssueSearchViewMetadata
			@argumentDefinitions(
				isIssueRankingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-issue-ranking.relayprovider"
				}
				isSavedListViewTechnicalChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-enabled.relayprovider"
				}
				isFrozenColumnsEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-frozen-columns-enabled.relayprovider.tsx"
				}
			) {
				...NativeIssueTableWithSelectedIssuesProvider
					@include(if: $isSavedListViewTechnicalChangesEnabled)
				__typename
				id @required(action: THROW)
				__id
				viewId @required(action: THROW)
				savedViewId @include(if: $isSavedListViewTechnicalChangesEnabled)
				hasDefaultFieldSets(scope: $fieldSetsScope)
				fieldSets(
					first: $amountOfColumns
					filter: { fieldSetSelectedState: SELECTED }
					scope: $fieldSetsScope
				) {
					__id
					totalCount
					edges {
						node {
							fieldSetId
							fieldSetPreferences {
								isFrozen @include(if: $isFrozenColumnsEnabled)
							}
						}
					}
					...NativeIssueTableWithSelectedIssuesProvider_columns
				}
				... on JiraSpreadsheetView {
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						canEnableHierarchy
						groupByConfig {
							groupByField {
								fieldId
							}
						}
						isHierarchyEnabled
						hideDone
						canEnableReranking @include(if: $isIssueRankingEnabled)
					}
					conditionalFormattingRules(first: 20)
						@include(if: $isSavedListViewTechnicalChangesEnabled) {
						...NativeIssueTableWithSelectedIssuesProvider_rules
					}
				}
				... on JiraIssueSearchViewMetadata {
					isViewConfigModified(settings: $settings)
						@include(if: $isSavedListViewTechnicalChangesEnabled)
				}
			}
		`,
		view,
	);

	// manually throw error when fieldsets is null
	if (viewData.fieldSets == null) {
		fireErrorAnalytics({
			meta: {
				id: 'NinNullFieldSets',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			sendToPrivacyUnsafeSplunk: false,
			skipSentry: true,
			attributes: {
				viewId: viewData.viewId,
				hasDefaultFieldSets: viewData.hasDefaultFieldSets,
				hasIssueResults: issueResultsData != null,
				hasView: view != null,
				typeName: viewData.__typename,
				viewResultError:
					viewResultError != null
						? `${viewResultError?.statusCode} ${viewResultError?.message}`
						: false,
				...((isInlineFieldConfigEnabled || fg('jira_nin_last_used_jql')) && {
					fieldSetsScope: {
						operationScope: getOperationScope(issueTableProps?.projectContext),
						projectKey: issueTableProps?.projectContext != null,
					},
				}),
			},
		});

		throw new Error(
			"Relay: Missing @required value at path 'fieldSets' in 'main_issueNavigator_ListView_view'.",
		);
	}

	const projectData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_ListView_project on JiraProject
			@argumentDefinitions(
				isConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
			) {
				key
				projectId
				...NativeIssueTableWithSelectedIssuesProvider_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project,
	);

	const filterData = useFragment<FilterFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_ListView_filter on JiraFilter {
				name
			}
		`,
		filter,
	);

	const rankFieldData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_ListView_rankField on JiraQuery
			@argumentDefinitions(
				isIssueRankingEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-issue-ranking.relayprovider"
				}
			) {
				rankField(cloudId: $cloudId) @include(if: $isIssueRankingEnabled) {
					...NativeIssueTableWithSelectedIssuesProvider_rankField
				}
			}
		`,
		rankField,
	);

	const selectedIssueKey = useSelectedIssueKey();
	const selectedIssueIndex = useSelectedIssueIndex();

	const currentPage = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? Math.ceil(
				(issueResultsData?.issueNavigatorPageInfo?.firstIssuePosition ?? MAX_ISSUES_PER_PAGE) /
					MAX_ISSUES_PER_PAGE,
			)
		: (issueResultsData?.pageCursors?.around?.find(
				(
					cursor:
						| {
								readonly isCurrent: boolean | null | undefined;
								readonly pageNumber: number | null | undefined;
						  }
						| null
						| undefined,
				) => cursor?.isCurrent,
			)?.pageNumber ?? 1);

	const [{ field, direction }, { setFieldAndDirection }] = useSortFieldAndDirection();

	// TODO JSC-242 Need to figure out appropriate refetch behaviour, e.g. do we want to try keep visible issues in view?
	const { refetch: onIssueSearchForColumnConfig, refresh } = useIssueSearchActions();

	const { isLoading: isColumnPickerLoading, setIsRestoringDefaultColumns } =
		useColumnPickerLoadingContext();

	const onModifyColumns = useCallback(() => {
		onChangeColumnConfiguration && onChangeColumnConfiguration();
		onIssueSearchForColumnConfig();
	}, [onChangeColumnConfiguration, onIssueSearchForColumnConfig]);

	const fieldSetIds = useMemo(
		() => viewData.fieldSets?.edges?.map((edge) => edge?.node?.fieldSetId).filter(Boolean) ?? [],
		[viewData.fieldSets?.edges],
	);

	const stickyColumnIds =
		isFrozenColumnSupportEnabled && fg('issue_table_frozen_columns')
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useMemo(
					() =>
						viewData.fieldSets?.edges
							?.filter((edge) => edge?.node?.fieldSetPreferences?.isFrozen === true)
							.map((edge) => edge?.node?.fieldSetId)
							.filter(Boolean) ?? [],
					[viewData.fieldSets.edges],
				)
			: [];

	const eventHandlers = useEventHandlers(issueResultsData?.__id, fieldSetIds);
	const overriddenTableComponents = issueTableProps?.components;
	const projectContext = issueTableProps?.projectContext;
	const isGroupingSupported = issueTableProps?.isGroupingSupported;

	const viewSettings = viewData?.viewSettings;

	/**
	 * isHierarchyEnabled reflects user preference in the current page,
	 * canEnableHierarchy reflects whether the hierarchy can be enabled based on the JQL / other settings,
	 * isIssueHierarchySupportEnabled reflects whether the hierarchy is supported in the experience.
	 * The combination of these three values determines whether issue hierarchy is enabled in issue table.
	 */
	const isHierarchyEnabled =
		viewSettings?.isHierarchyEnabled &&
		viewSettings.canEnableHierarchy &&
		isIssueHierarchySupportEnabled;

	const isHideDoneEnabled = viewSettings?.hideDone ?? false;
	const groupByFieldId = viewData?.viewSettings?.groupByConfig?.groupByField?.fieldId;

	const infiniteScrollProps = useInfiniteScroll({
		isHierarchyEnabled,
		fieldSetIds,
		groupBy: isGroupingSupported ? groupByFieldId : undefined,
		view: views.list,
		isIssueHierarchySupportEnabled,
		isHideDoneEnabled,
	});

	const onSortOrderChange = useCallback(
		(sortField: string, sortDirection: SortDirection, aliases: string[] = []) =>
			setFieldAndDirection(sortField, sortDirection, aliases),
		[setFieldAndDirection],
	);

	const hasMaxSelectedColumns = viewData?.fieldSets?.totalCount
		? viewData?.fieldSets?.totalCount >= MAX_COLUMNS
		: false;

	const isAnonymous = useIsAnonymous();

	const { showFlag } = useFlagService();

	const [replaceSpreadsheetViewFieldSetsOld] =
		useMutation<ReplaceSpreadsheetViewFieldSetsOldMutation>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				mutation main_replaceSpreadsheetViewFieldSetsOldMutation(
					$id: ID!
					$fieldSetsInput: JiraFieldSetsMutationInput
					$amountOfColumns: Int!
					$includeView: Boolean!
					$fieldSetsScope: JiraIssueSearchScope
				) @raw_response_type {
					jira {
						replaceSpreadsheetViewFieldSets(fieldSetsInput: $fieldSetsInput, id: $id)
							@optIn(to: "JiraIssueSearchView") {
							success
							view @include(if: $includeView) {
								hasDefaultFieldSets(scope: $fieldSetsScope)
								fieldSets(
									first: $amountOfColumns
									filter: { fieldSetSelectedState: SELECTED }
									scope: $fieldSetsScope
								) {
									__id
									totalCount
									edges {
										node {
											fieldSetId
										}
									}
									...NativeIssueTableWithSelectedIssuesProvider_columns
								}
							}
						}
					}
				}
			`,
		);

	const [replaceSpreadsheetViewFieldSets] = useMutation<ReplaceSpreadsheetViewFieldSetsMutation>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			mutation main_replaceSpreadsheetViewFieldSetsMutation(
				$id: ID!
				$fieldSetsInput: JiraFieldSetsMutationInput
				$amountOfColumns: Int!
				$includeView: Boolean!
				$fieldSetsScope: JiraIssueSearchScope
				$settings: JiraIssueSearchSettings
			) @raw_response_type {
				jira {
					replaceSpreadsheetViewFieldSets(fieldSetsInput: $fieldSetsInput, id: $id)
						@optIn(to: "JiraIssueSearchView") {
						success
						view @include(if: $includeView) {
							hasDefaultFieldSets(scope: $fieldSetsScope)
							fieldSets(
								first: $amountOfColumns
								filter: { fieldSetSelectedState: SELECTED }
								scope: $fieldSetsScope
							) {
								__id
								totalCount
								edges {
									node {
										fieldSetId
									}
								}
								...NativeIssueTableWithSelectedIssuesProvider_columns
							}
						}
						view {
							isViewConfigModified(settings: $settings)
						}
					}
				}
			}
		`,
	);

	/*
	 * This updater imperatively updates the isViewConfigModified record to handle the
	 * scenario where the variables provided to isViewConfigModified in the preloaded query
	 * differ from the variables provided in refetches.
	 *
	 * This ensures the save/reset view button is correctly hidden when publishing the view
	 * immediately after a page refresh.
	 *
	 * In all other scenarios we can rely on Relay writing the network response to the store
	 * via spreading a fragment which contains the isViewConfigModified field.
	 */
	const replaceSpreadsheetViewFieldSetsUpdater = useCallback<
		SelectorStoreUpdater<main_replaceSpreadsheetViewFieldSetsMutation$data>
	>(
		(store, response) => {
			const isViewConfigModified =
				response?.jira?.replaceSpreadsheetViewFieldSets?.view?.isViewConfigModified;
			if (
				response?.jira?.replaceSpreadsheetViewFieldSets?.success !== true ||
				isViewConfigModified == null
			) {
				return;
			}

			const viewRecord = store.get(viewData.__id);
			if (viewRecord == null) {
				return;
			}

			setIsViewConfigModifiedRecord({
				isViewConfigModified,
				viewRecord,
			});
		},
		[viewData.__id],
	);

	const onColumnsChange = useCallback<NonNullable<IssueTableProps['onColumnsChange']>>(
		(
			columnIds,
			changeType,
			optimisticFieldSets,
			onSuccessCallback,
			onErrorCallback,
			retries = 0,
			analyticsAttributes,
			skipRefetch,
		) => {
			let attempts = 0;

			// This handler can be called from a native event listener hence React does not batch state updates.
			// We manually batch this update for better rendering performance and more accurate measurement of
			// drag-and-drop interaction performance. We can remove this with React 18's automatic batching.
			ReactDOM.unstable_batchedUpdates(() => {
				if (changeType === REORDER_COLUMNS) {
					dragAndDropExperience.start();
				}

				const handleCompleted = (success?: boolean) => {
					if (success) {
						const canSkipRefetch = skipRefetch && fg('jira_inline_field_config_gate');

						if (changeType === MODIFY_COLUMNS && !canSkipRefetch) {
							onModifyColumns();
						} else if (changeType === REORDER_COLUMNS) {
							dragAndDropExperience.success();
						}
						if (fg('jira_inline_field_config_gate')) {
							const eventAttributes = {
								...analyticsAttributes,
								operationScope: getOperationScope(projectContext),
							};
							if (changeType === MODIFY_COLUMNS) {
								fireTrackAnalytics(
									createAnalyticsEvent({}),
									'issueTableColumns updated',
									eventAttributes || {},
								);
							} else if (changeType === REORDER_COLUMNS) {
								fireTrackAnalytics(createAnalyticsEvent({}), 'issueTableColumns updated', {
									actionType: REORDER_COLUMNS,
									operationScope: getOperationScope(projectContext),
								});
							}
						} else {
							const totalCount = viewData?.fieldSets?.totalCount;
							if (changeType === MODIFY_COLUMNS) {
								fireTrackAnalytics(createAnalyticsEvent({}), 'issueTableColumns updated', {
									isInline: false,
									actionType: columnIds.length < (totalCount ?? 0) ? REMOVE_COLUMNS : ADD_COLUMNS,
								});
							} else if (changeType === REORDER_COLUMNS) {
								fireTrackAnalytics(createAnalyticsEvent({}), 'issueTableColumns updated', {
									actionType: REORDER_COLUMNS,
								});
							}
						}

						if (fg('jira_inline_field_config_gate')) {
							onSuccessCallback?.();
						}
					} else {
						const showError = !fg('jira_inline_field_config_gate') || attempts === retries;

						if (showError) {
							showFlag(reorderColumnsErrorFlag);
							fireErrorAnalytics({
								meta: {
									id: 'commitReplaceListViewFieldSetsMutationUnsuccessful',
									packageName: PACKAGE_NAME,
									teamName: TEAM_NAME,
								},
								sendToPrivacyUnsafeSplunk: true,
								attributes: {
									changeType,
									retries,
								},
							});

							if (changeType === REORDER_COLUMNS) {
								dragAndDropExperience.failure();
							}

							if (fg('jira_inline_field_config_gate')) {
								onErrorCallback?.();
							}
						} else {
							attempts++;
							commitFieldSets();
						}
					}
				};

				const handleError = (error: Error) => {
					const showError = !fg('jira_inline_field_config_gate') || attempts === retries;

					if (showError) {
						showFlag(reorderColumnsErrorFlag);
						fireErrorAnalytics({
							meta: {
								id: 'commitReplaceListViewFieldSetsMutationError',
								packageName: PACKAGE_NAME,
								teamName: TEAM_NAME,
							},
							error,
							sendToPrivacyUnsafeSplunk: true,
							attributes: {
								changeType,
							},
						});
						if (changeType === REORDER_COLUMNS) {
							dragAndDropExperience.failure();
						}
						if (fg('jira_inline_field_config_gate')) {
							onErrorCallback?.();
						}
					} else {
						attempts++;
						commitFieldSets();
					}
				};

				const commitFieldSets = () => {
					if (fg('saved_list_view_technical_changes')) {
						const savedViewId = viewData.savedViewId;
						if (savedViewId == null) {
							throw new Error('Missing saved view ID');
						}

						const variables = {
							id: savedViewId,
							amountOfColumns: MAX_COLUMNS,
							fieldSetsInput: {
								replaceFieldSetsInput: {
									nodes: columnIds,
									...(projectData?.projectId &&
										fg('jira_nin_last_used_jql') && {
											context: {
												projectContext: {
													project: projectData?.projectId,
												},
											},
										}),
									...(isInlineFieldConfigEnabled &&
										!fg('jira_nin_last_used_jql') && {
											context: {
												projectContext: {
													project: projectData?.projectId,
												},
											},
										}),
								},
							},
							includeView: changeType === REORDER_COLUMNS,
							...(fg('jira_nin_last_used_jql') && {
								fieldSetsScope: {
									operationScope: getOperationScope(projectContext),
									projectKey: projectData?.key ?? null,
								},
							}),
							...(isInlineFieldConfigEnabled &&
								!fg('jira_nin_last_used_jql') && {
									fieldSetsScope: {
										operationScope: getOperationScope(projectContext),
										projectKey: projectData?.key,
									},
								}),
							settings: {
								groupBy: viewSettings?.groupByConfig?.groupByField?.fieldId ?? null,
								hideDone: Boolean(viewSettings?.hideDone),
								hierarchyEnabled: Boolean(viewSettings?.isHierarchyEnabled),
								jql: resolvedJql,
							},
						};

						replaceSpreadsheetViewFieldSets({
							variables,
							optimisticResponse:
								changeType === REORDER_COLUMNS && optimisticFieldSets
									? {
											jira: {
												replaceSpreadsheetViewFieldSets: {
													success: true,
													view: {
														__typename: viewData.__typename,
														id: viewData.id,
														hasDefaultFieldSets: viewData.hasDefaultFieldSets,
														fieldSets: {
															...optimisticFieldSets,
														},
														isViewConfigModified: viewData.isViewConfigModified,
													},
												},
											},
										}
									: undefined,
							updater: replaceSpreadsheetViewFieldSetsUpdater,
							onCompleted(data) {
								handleCompleted(data.jira?.replaceSpreadsheetViewFieldSets?.success);
							},
							onError(error) {
								handleError(error);
							},
						});
					} else {
						const variables = {
							id: viewData.id,
							amountOfColumns: MAX_COLUMNS,
							fieldSetsInput: {
								replaceFieldSetsInput: {
									nodes: columnIds,
									...(projectData?.projectId &&
										fg('jira_nin_last_used_jql') && {
											context: {
												projectContext: {
													project: projectData?.projectId,
												},
											},
										}),
									...(isInlineFieldConfigEnabled &&
										!fg('jira_nin_last_used_jql') && {
											context: {
												projectContext: {
													project: projectData?.projectId,
												},
											},
										}),
								},
							},
							includeView: changeType === REORDER_COLUMNS,
							...(fg('jira_nin_last_used_jql') && {
								fieldSetsScope: {
									operationScope: getOperationScope(projectContext),
									projectKey: projectData?.key ?? null,
								},
							}),
							...(isInlineFieldConfigEnabled &&
								!fg('jira_nin_last_used_jql') && {
									fieldSetsScope: {
										operationScope: getOperationScope(projectContext),
										projectKey: projectData?.key,
									},
								}),
						};

						replaceSpreadsheetViewFieldSetsOld({
							variables,
							optimisticResponse:
								changeType === REORDER_COLUMNS && optimisticFieldSets
									? {
											jira: {
												replaceSpreadsheetViewFieldSets: {
													success: true,
													view: {
														__typename: viewData.__typename,
														id: viewData.id,
														hasDefaultFieldSets: viewData.hasDefaultFieldSets,
														fieldSets: {
															...optimisticFieldSets,
														},
													},
												},
											},
										}
									: undefined,
							onCompleted(data) {
								handleCompleted(data.jira?.replaceSpreadsheetViewFieldSets?.success);
							},
							onError(error) {
								handleError(error);
							},
						});
					}
				};

				commitFieldSets();
			});
		},
		[
			onModifyColumns,
			projectContext,
			createAnalyticsEvent,
			viewData?.fieldSets?.totalCount,
			viewData.savedViewId,
			viewData.__typename,
			viewData.id,
			viewData.hasDefaultFieldSets,
			viewData.isViewConfigModified,
			showFlag,
			isInlineFieldConfigEnabled,
			projectData?.projectId,
			projectData?.key,
			viewSettings?.groupByConfig?.groupByField?.fieldId,
			viewSettings?.hideDone,
			viewSettings?.isHierarchyEnabled,
			resolvedJql,
			replaceSpreadsheetViewFieldSets,
			replaceSpreadsheetViewFieldSetsUpdater,
			replaceSpreadsheetViewFieldSetsOld,
		],
	);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [updateFieldSetPreferencesOld] = useMutation<UpdateFieldSetPreferencesOldMutation>(graphql`
		mutation main_updateFieldSetPreferencesOldMutation(
			$cloudId: ID!
			$input: JiraFieldSetPreferencesMutationInput!
			$viewId: ID
			$context: JiraIssueSearchViewFieldSetsContext
		) {
			jira {
				updateUserFieldSetPreferences(
					cloudId: $cloudId
					fieldSetPreferencesInput: $input
					viewId: $viewId
					context: $context
				) @optIn(to: "JiraUpdateUserFieldSetPreferences") {
					success
				}
			}
		}
	`);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [updateFieldSetPreferences] = useMutation<UpdateFieldSetPreferencesMutation>(graphql`
		mutation main_updateFieldSetPreferencesMutation(
			$cloudId: ID!
			$input: JiraFieldSetPreferencesMutationInput!
			$viewId: ID
			$context: JiraIssueSearchViewFieldSetsContext
			$settings: JiraIssueSearchSettings
		) {
			jira {
				updateUserFieldSetPreferences(
					cloudId: $cloudId
					fieldSetPreferencesInput: $input
					viewId: $viewId
					context: $context
				) @optIn(to: "JiraUpdateUserFieldSetPreferences") {
					success
					errors {
						message
						extensions {
							errorType
							statusCode
						}
					}
					view {
						... on JiraIssueSearchViewMetadata {
							isViewConfigModified(settings: $settings)
						}
					}
				}
			}
		}
	`);

	const cloudId = useCloudId();

	const updateColumnWidth = useColumnWidthUpdater(viewData.fieldSets?.__id);
	const updateColumnIsFrozen = fg('issue_table_frozen_columns')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useColumnIsFrozenUpdater(viewData.fieldSets?.__id)
		: undefined;

	const { commitUpdateFieldSetPreferences, commitUpdateFieldSetPreferencesOld } =
		useCommitFieldSetPreferences({
			cloudId,
			projectId: projectData?.projectId,
			viewId: viewData.id,
			isInlineFieldConfigEnabled,
			updateFieldSetPreferences,
			updateFieldSetPreferencesOld,
			settings: {
				groupBy: viewSettings?.groupByConfig?.groupByField?.fieldId ?? null,
				hideDone: Boolean(viewSettings?.hideDone),
				hierarchyEnabled: Boolean(viewSettings?.isHierarchyEnabled),
				jql: resolvedJql,
			},
			relayViewId: viewData.__id,
		});

	const onColumnFreeze = useCallback<NonNullable<IssueTableProps['onColumnFreeze']>>(
		(payload) => {
			if (!isAnonymous) {
				if (fg('saved_list_view_technical_changes')) {
					const savedViewId = viewData.savedViewId;
					if (savedViewId == null) {
						throw new Error('Missing saved view ID');
					}
					commitUpdateFieldSetPreferences(savedViewId, payload, 'isFrozen');
				} else {
					commitUpdateFieldSetPreferencesOld(payload, 'isFrozen');
				}
			}
			// commit local update to the store independently of the mutation result to preserve user's desired frozen state
			updateColumnIsFrozen?.(payload);
		},
		[
			commitUpdateFieldSetPreferences,
			commitUpdateFieldSetPreferencesOld,
			isAnonymous,
			updateColumnIsFrozen,
			viewData.savedViewId,
		],
	);

	const onColumnResize = useCallback<NonNullable<IssueTableProps['onColumnResize']>>(
		(columnId, width) => {
			// skip mutation for anonymous users as they don't have access to user preferences
			if (!isAnonymous) {
				// we intentionally don't optimistically update the store to avoid rolling back column widths
				// in case of a mutation error, which would be very jarring for the user
				if (fg('saved_list_view_technical_changes')) {
					const savedViewId = viewData.savedViewId;
					if (savedViewId == null) {
						throw new Error('Missing saved view ID');
					}

					commitUpdateFieldSetPreferences(
						savedViewId,
						[
							{
								fieldSetId: columnId,
								width: width ?? null,
							},
						],
						'width',
					);
				} else {
					commitUpdateFieldSetPreferencesOld(
						[
							{
								fieldSetId: columnId,
								width: width ?? null,
							},
						],
						'width',
					);
				}
			}

			// commit local update to the store independently of the mutation result to preserve user's desired width
			updateColumnWidth(columnId, width);
		},
		[
			isAnonymous,
			updateColumnWidth,
			viewData.savedViewId,
			commitUpdateFieldSetPreferences,
			commitUpdateFieldSetPreferencesOld,
		],
	);

	useLayoutEffect(() => {
		dragAndDropExperience.markInlineResponse();
	});

	const contextValue = useMemo(
		() => ({
			hasMaxSelectedColumns,
			filterName: filterData?.name,
		}),
		[filterData?.name, hasMaxSelectedColumns],
	);

	const onRestoreDefaultColumns = useCallback<
		NonNullable<IssueTableProps['onRestoreDefaultColumns']>
	>(() => {
		setIsRestoringDefaultColumns(true);

		const handleCompleted = (success?: boolean) => {
			setIsRestoringDefaultColumns(false);
			if (success) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'issueTableColumns updated', {
					actionType: RESTORE_COLUMNS,
					operationScope: getOperationScope(projectContext),
				});

				refresh();
			} else {
				showFlag(resetColumnsErrorFlag);
				fireErrorAnalytics({
					meta: {
						id: 'commitReplaceListViewUsingResetFieldSetsMutationUnsuccessful',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		};

		const handleError = (error: Error) => {
			setIsRestoringDefaultColumns(false);
			showFlag(resetColumnsErrorFlag);
			fireErrorAnalytics({
				meta: {
					id: 'commitReplaceListViewUsingResetFieldSetsMutationError',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		};

		if (fg('saved_list_view_technical_changes')) {
			const savedViewId = viewData.savedViewId;
			if (savedViewId == null) {
				throw new Error('Missing saved view ID');
			}

			const variables = {
				id: savedViewId,
				amountOfColumns: MAX_COLUMNS,
				includeView: true,
				...(isInlineFieldConfigEnabled
					? {
							fieldSetsInput: {
								scopedResetToDefaultFieldSets: {
									doReset: true,
									context: {
										projectContext: {
											project: projectData?.projectId,
										},
									},
								},
							},
						}
					: {
							fieldSetsInput: { resetToDefaultFieldSets: true },
						}),
				settings: {
					groupBy: viewSettings?.groupByConfig?.groupByField?.fieldId ?? null,
					hideDone: Boolean(viewSettings?.hideDone),
					hierarchyEnabled: Boolean(viewSettings?.isHierarchyEnabled),
					jql: resolvedJql,
				},
			};

			replaceSpreadsheetViewFieldSets({
				variables,
				updater: replaceSpreadsheetViewFieldSetsUpdater,
				onCompleted(data) {
					handleCompleted(data.jira?.replaceSpreadsheetViewFieldSets?.success);
				},
				onError(error) {
					handleError(error);
				},
			});
		} else {
			const variables = {
				id: viewData.id,
				amountOfColumns: MAX_COLUMNS,
				includeView: true,
				...(isInlineFieldConfigEnabled
					? {
							fieldSetsInput: {
								scopedResetToDefaultFieldSets: {
									doReset: true,
									context: {
										projectContext: {
											project: projectData?.projectId,
										},
									},
								},
							},
						}
					: {
							fieldSetsInput: { resetToDefaultFieldSets: true },
						}),
			};

			replaceSpreadsheetViewFieldSetsOld({
				variables,
				onCompleted(data) {
					handleCompleted(data.jira?.replaceSpreadsheetViewFieldSets?.success);
				},
				onError(error) {
					handleError(error);
				},
			});
		}
	}, [
		setIsRestoringDefaultColumns,
		createAnalyticsEvent,
		projectContext,
		refresh,
		showFlag,
		viewData.savedViewId,
		viewData.id,
		isInlineFieldConfigEnabled,
		projectData?.projectId,
		viewSettings?.groupByConfig?.groupByField?.fieldId,
		viewSettings?.hideDone,
		viewSettings?.isHierarchyEnabled,
		resolvedJql,
		replaceSpreadsheetViewFieldSets,
		replaceSpreadsheetViewFieldSetsUpdater,
		replaceSpreadsheetViewFieldSetsOld,
	]);

	/**
	 * returns undefined if there are default field sets
	 * returns undefined if the user is on filter tab but cannot edit filter
	 */
	const conditionalOnRestoreDefaults = useConditionalOnRestoreDefaults(
		onRestoreDefaultColumns,
		viewData?.hasDefaultFieldSets,
		fg('jira_inline_field_config_gate') ? viewData?.viewId : undefined,
	);

	const tableComponents = useMemo(
		// We shallow merge these objects to make it easier for issue-navigator consumers to revert custom column
		// picker components, which are very NIN specific. This decision can be reconsidered if we need to introduce
		// additional nested custom components in the issue table and make those available to all consumers.
		() => ({
			columnPicker: {
				Container: ({ children }: { children: React.ReactNode }) => (
					<ColumnPickerRemountManager
						viewIdFromResponse={parseIssueNavigatorViewIdOrDefault(viewData.viewId)}
					>
						{children}
					</ColumnPickerRemountManager>
				),
				// eslint-disable-next-line jira/ff/no-preconditioning
				...(fg('saved_list_view_technical_changes') && projectContext
					? {}
					: {
							PopupComponent: fg('jira_inline_field_config_gate')
								? createPopupComponent(conditionalOnRestoreDefaults)
								: ColumnPickerPopupComponent,
						}),
			},
			IssueCount,
			NoColumns,
			NoIssues,
			...overriddenTableComponents,
			ConnectionsSubscriber: ({
				connections,
				updateConnectionDetails,
			}: ConnectionsSubscriberProps) => (
				<BulkOpsConnectionsSubscriber
					connections={connections}
					updateConnectionDetails={updateConnectionDetails}
					fragmentRef={issueResultsData || groupResultsData?.firstGroup?.issues || null}
				/>
			),
		}),
		[
			projectContext,
			conditionalOnRestoreDefaults,
			overriddenTableComponents,
			viewData.viewId,
			issueResultsData,
			groupResultsData?.firstGroup?.issues,
		],
	);

	const conditionalOnChange: typeof onColumnsChange | undefined =
		useConditionalOnChange(onColumnsChange);

	const conditionalColumnLoader: IssueTableProps['useColumnLoader'] | undefined =
		useConditionalColumnLoader();

	const [isIFCEnabled, ifcConfig] = isInlineFieldConfigEnabled
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIFCSidebar({
				projectKey: projectData?.key,
				projectId: projectData?.projectId,
				issues: issueResultsData ?? undefined,
			})
		: [false, undefined];

	const resultsFragment = exclusiveGroupsOrIssuesFragment({
		groups: groupResultsData ?? null,
		issues: issueResultsData ?? null,
	});
	const isConditionalFormattingEnabled =
		features.isConditionalFormattingEnabled && fg('jira_list_conditional_formatting');

	const formattingRules =
		isConditionalFormattingEnabled && fg('saved_list_view_technical_changes')
			? (viewData?.conditionalFormattingRules ?? null)
			: null;

	if (!resultsFragment) {
		throw new Error('Missing groups or issues data');
	}

	return (
		<UFOLabel name="list-view">
			<DialogContextContainer>
				<ContextualAnalyticsData attributes={{ currentPage, isAnonymous }}>
					{resultsFragment.issues && (
						<AsyncRegisterShortcutDialogActions
							issues={resultsFragment.issues}
							selectedIssueKey={selectedIssueKey}
							eventHandlers={eventHandlers}
						/>
					)}
					<ColumnPickerMessageContext.Provider value={contextValue}>
						<TableContainer>
							<AsyncNativeIssueTable
								view={fg('saved_list_view_technical_changes') ? viewData : null}
								columns={viewData.fieldSets}
								onColumnsChange={conditionalOnChange}
								onRestoreDefaultColumns={conditionalOnRestoreDefaults}
								useColumnLoader={conditionalColumnLoader}
								loading={loading}
								eventHandlers={eventHandlers}
								onSelectedRowChange={onSelectedRowChange}
								selectedRow={selectedIssueIndex}
								sortField={field}
								sortDirection={direction}
								onSortChange={onSortOrderChange}
								onRefetch={onIssueSearchForColumnConfig}
								// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
								components={tableComponents}
								issueSearchBaseUrl="" // Relative to current route rendering the issue navigator
								isColumnPickerLoading={isColumnPickerLoading}
								projectContext={projectContext}
								isGroupingSupported={isGroupingSupported}
								isIssueCreateEnabled // It's already tied to the project context
								onColumnResize={onColumnResize}
								project={projectData}
								rankField={rankFieldData?.rankField}
								isIssueHierarchyEnabled={Boolean(isHierarchyEnabled)}
								isGridNavigationEnabled={expVal(
									'nin_grid_navigation_experiment',
									'isGridNavigationEnabled',
									false,
								)}
								isInlineEditingExtendedFieldSupportEnabled={expVal(
									'jira_list_inline_editing',
									'isInlineEditingEnabled',
									false,
								)}
								isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
								isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
								ifcConfig={
									isIFCEnabled || fg('jsc_list_category_editing_in_nin') ? ifcConfig : undefined
								}
								onIssueFlatListUpdated={onIssueFlatListUpdated}
								{...(fg('jsc_m2_hierarchy_fe_changes') && { jql: resolvedJql })}
								enableMultiSelect
								{...(expVal('jira_list_reparenting', 'isReparentingEnabled', false) && {
									isReparentingEnabled: true,
								})}
								{...(expVal('jira_list_single_line_row_height', 'isEnabled', false) && {
									isSingleLineRowHeightEnabled: true,
								})}
								{...(expVal('jira_list_hide_done_items', 'isEnabled', false) && {
									isHideDoneItemsEnabled: isHideDoneEnabled,
								})}
								isMergedColumnEnabled={
									isMergedColumnSupportEnabled && fg('jira_list_merged_column_gate')
								}
								formattingRules={formattingRules}
								{...infiniteScrollProps}
								{...resultsFragment}
								preloadedEntryPoints={issueTableProps?.preloadedEntryPoints}
								{...(fg('jsc_list_reranking_gate') && {
									isIssueRankingEnabled:
										(isIssueRankingSupportEnabled && viewSettings?.canEnableReranking) ?? false,
								})}
								isIssueOperationMeatballMenuEnabled={isRowContextMenuEnabled}
								{...(isFrozenColumnSupportEnabled &&
									fg('issue_table_frozen_columns') && {
										isStickyColumnsEnabled: true,
										stickyColumnIds,
										onColumnFreeze,
										enableAutoUnstickyOnSmallScreen: true,
									})}
								isConditionalFormattingEnabled={isConditionalFormattingEnabled}
								isProjectQuickstartOnboardingEnabled={
									features.isProjectQuickstartOnboardingEnabled && fg('jira_list_nin_merge')
								}
							/>
						</TableContainer>
						{projectData?.projectId &&
							projectData?.key &&
							fg('in_context_config_sidebar_changeboarding') && (
								<NumberFormattingChangeboardingWrapper
									projectId={String(projectData.projectId)}
									projectKey={projectData.key}
								/>
							)}
					</ColumnPickerMessageContext.Provider>
				</ContextualAnalyticsData>
				<AsyncAttachFileDialog />
				<FireScreenAnalytics />
			</DialogContextContainer>
		</UFOLabel>
	);
};

// re-render regression detection
ListView.whyDidYouRender = true;

const ListViewWithColumnPickerContext = (props: Props) => (
	<ColumnPickerLoadingContextProvider>
		<ListView {...props} />
	</ColumnPickerLoadingContextProvider>
);

export default withReportErrors<Props>(ListViewWithColumnPickerContext, {
	id: 'ui.issue-results.list-view.unhandled',
	packageName: PACKAGE_NAME,
	teamName: TEAM_NAME,
	sendToPrivacyUnsafeSplunk: true,
	attributes: isLoaderErrorAttributes,
});

const TableContainerStyles = xcss({
	flexGrow: 1,
	flexBasis: 0,
	display: 'flex',
	height: '100%',
	overflow: 'hidden',
	flexDirection: 'column',
});
